import { InputContentPart, Message, UserMessage } from '@ag-ui/core';
import { isNil, isString } from 'lodash';
import { AskPromptAttachment } from '../api';
import { toAskPromptAttachments } from '../util/askAttachmentParts';

/**
 * The text of a user turn. AG-UI allows content to be multimodal parts as well as a
 * plain string; only text can go on the wire as a prompt, so anything else is dropped.
 */
export function userText(content: string | InputContentPart[]): string {
  if (isString(content)) {
    return content;
  }
  return content
    .filter(part => part.type === 'text')
    .map(part => part.text)
    .join('\n');
}

/**
 * The turn being asked. The gateway holds the conversation itself (see
 * `AskPromptRequest.threadId`), so a request carries only the newest question.
 *
 * Anything after the newest user message is ignored: at request time the message just
 * added by `send` is always last, and the only other producer (the dev sample-html
 * command) never issues a request.
 *
 * A user turn with no text at all (an image on its own) yields no prompt rather than
 * falling further back, which would silently re-ask an older question.
 */
function lastUserMessage(messages: Message[]): UserMessage | undefined {
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const message = messages[i];
    if (message.role === 'user') {
      return message;
    }
  }
  return undefined;
}

/** The newest user turn's text, as the request's `prompt`. */
export function lastUserPrompt(messages: Message[]): string {
  const message = lastUserMessage(messages);
  return isNil(message) ? '' : userText(message.content);
}

/**
 * The files the newest user turn carries, as the request's `attachments`. Read back off the
 * turn rather than from anything held alongside it, so an aborted-and-retried run carries
 * exactly the files its question shows.
 */
export function lastUserAttachments(messages: Message[]): AskPromptAttachment[] {
  const message = lastUserMessage(messages);
  return isNil(message) ? [] : toAskPromptAttachments(message.content);
}
